import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PersonalizedProductsQuery, RecommendedProductsQuery } from 'src/personalization/data-layer/types';

import { ProductCarousel } from 'src/components/carousel-v2/types/product-carousel';
import { RecommendationsConciergeCard } from '../recommendations-concierge-card';
import { usePersonalizedProductsCarousel } from '../../use-personalized-products-carousel';
import { useBuyItAgainProducts } from '../../use-buy-it-again-products';
import { UseWidgetOrdersReturn } from '../use-widget-orders';
import { BuyItAgain } from './buy-it-again';

type BlendedPersonalizedProductCarouselProps = {
  heading?: string;
  icon?: React.ReactNode;
  personalizedProductsQuery: PersonalizedProductsQuery;
  recommendedProductsQuery: RecommendedProductsQuery;
  subheading?: string;
  widgetOrders: UseWidgetOrdersReturn;
};

export const BlendedPersonalizedProductCarousel = ({
  heading,
  icon,
  personalizedProductsQuery,
  recommendedProductsQuery,
  subheading,
  widgetOrders,
}: BlendedPersonalizedProductCarouselProps): JSX.Element | null => {
  const {
    handleProductClick,
    handleQuickAddToCart,
    products,
    theme,
    error,
    viewportVisibility,
  } = usePersonalizedProductsCarousel({ personalizedProductsQuery });

  const flags = useFlags();
  const isBuyItAgainCarouselEnabled = Boolean(flags['ecomm.buy-it-again-carousel.rollout']);

  const buyItAgainProducts = useBuyItAgainProducts({
    recommendedProductsQuery,
  });
  const { loading: buyItAgainLoading, error: buyItAgainError } = buyItAgainProducts;

  const { orders, ordersLoading, reorder, loadingOrderId, handleViewOrder } = widgetOrders;

  if (buyItAgainError) {
    console.error("Error fetching user's previously purchased products.", buyItAgainError);
  }

  const isLoading = !isBuyItAgainCarouselEnabled && products.length > 0 && (buyItAgainLoading || ordersLoading);

  if (error || !products.length) {
    return null;
  }

  return (
    <div data-testid='personalized-products-carousel'>
      <ProductCarousel
        ref={viewportVisibility.ref}
        products={products}
        isLoading={isLoading}
        title={heading}
        analyticsSection='Recommended for you'
        subTitle={subheading}
        icon={icon}
        beforeProducts={
          <>
            <RecommendationsConciergeCard />
            {!isBuyItAgainCarouselEnabled && (
              <BuyItAgain
                theme={theme}
                {...buyItAgainProducts}
                orders={orders}
                reorder={reorder}
                loadingOrderId={loadingOrderId}
                handleViewOrder={handleViewOrder}
              />
            )}
          </>
        }
        onProductClick={handleProductClick}
        onQuickAddToCart={handleQuickAddToCart}
      />
    </div>
  );
};
